import { connect } from "react-redux";
import { useCallback, useEffect, useState } from "react";
import { useListSearch } from "../../../common/hooks";
import TooltipButton from "../../../common/tooltipButton";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import CustomBreadCrumbs from "../../../common/breadcrumbs";
import { useIntl } from "react-intl";
import List from "../../../common/list";
import { TextInput } from "../../../common/fields";
import * as ListCellRenders from "../../../common/list/ListCellRenders";
import {
  listRequest,
  setQuery,
  deleteRequest,
} from "../../../../store/slices/rewardSlice";
import * as RoleHelper from "../../../../helpers/RoleHelper";
import { AccessScopeConstants, RouteConstants } from "../../../../constants";
import { useNavigate } from "react-router-dom";

const RewardList = ({
  action,
  labels,
  list,
  listRequest,
  query,
  requesting,
  setQuery,
  user,
  totalCount,
  deleteRequest,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [namePattern, setNamePattern] = useListSearch(
    query?.namePattern,
    (value) => setQuery({ namePattern: value, pageNo: 1 })
  );
  const [createdByNamePattern, setCreatedByNamePattern] = useListSearch(
    query?.createdBy,
    (value) => setQuery({ ...query, createdBy: value, pageNo: 1 })
  );

  const { role } = user || {};

  const canAdd = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.ContentAccess,
    AccessScopeConstants.CreateScope
  );

  const canDelete = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.ContentAccess,
    AccessScopeConstants.DeleteScope
  );

  const fetchList = useCallback(() => {
    if (query) {
      listRequest({
        ...query,
        sortColumn: query.sortColumn || "name",
      });
    }
  }, [query, listRequest]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    if (action === "create" || action === "update" || action === "delete") {
      fetchList();
    }
  }, [action, fetchList]);

  const onAddClick = () => {
    navigate(RouteConstants.APP_ROUTE_REWARDS_ADD);
  };

  const onDeleteClick = (record) => {
    if (record && record.id) {
      confirmDialog({
        message: intl.formatMessage(
          { id: "delete_object_message" },
          {
            name: record.name,
            object: intl.formatMessage({ id: "reward" }),
          }
        ),
        header: intl.formatMessage({ id: "delete_confirmation" }),
        icon: "pi pi-info-circle",
        defaultFocus: "reject",
        acceptClassName: "p-button-danger",
        rejectClassName: "p-button-secondary",
        accept: () => {
          deleteRequest({ id: record.id });
        },
        reject: () => {},
      });
    }
  };

  // Function removed since rewards no longer have types
  // const getTypeLabel = (type) => {
  //   if (!type) return "-";
  //   const typeConfig = RewardConstants.REWARD_TYPES.find(
  //     (t) => t.value === type
  //   );
  //   return typeConfig ? intl.formatMessage({ id: typeConfig.name }) : type;
  // };

  const columns = [
    {
      name: "name",
      header: "name",
      sortable: true,
      body: ListCellRenders.NameAndLogo("name", "artifactUrl"),
    },
    {
      name: "awardedAvailable",
      header: "reward_usage", // add i18n key reward_usage
      sortable: false,
      body: (rowData) => {
        if (!rowData) return "-";
        const awarded = rowData.totalRewardsAwarded ?? 0;
        const availableRaw = rowData.totalRewardsAvailable;
        const available = availableRaw === 0 ? "∞" : availableRaw ?? "∞";
        return `${awarded}/${available}`;
      },
    },
    // pointsValue removed (no longer applicable)
    {
      name: "isActive",
      header: "status",
      body: ListCellRenders.Status,
    },
    {
      name: "createdByUser",
      header: "created_by",
      sortable: false,
      body: ListCellRenders.CreatedBy,
    },
    {
      name: "createdAt",
      header: "created_at",
      sortable: true,
      body: ListCellRenders.DateTime("createdAt"),
    },
    {
      name: "updatedAt",
      header: "updated_at",
      sortable: true,
      body: ListCellRenders.DateTime("updatedAt"),
    },
  ];

  columns.push({
    name: "actions",
    headerIcon: "pi-cog",
    align: "center",
    body: ListCellRenders.OtherActions([
      {
        label: "delete",
        icon: "pi pi-trash",
        isAllowed: canDelete,
        // If reward has associations (e.g., used in programs / objects) don't allow delete
        additionalCheckNotAllowedMessage: (data) =>
          data.hasAssociations
            ? "reward_has_associations_delete"
            : "reward_delete_not_allowed",
        additionalCheckForAllowed: (data) =>
          !data.hasAssociations && data.canBeDeleted !== false,
        action: (data) => onDeleteClick(data),
      },
    ]),
  });

  return (
    <div>
      <CustomBreadCrumbs labels={labels} navigate={navigate} />
      <div className="edexlms-page-content">
        <div className="edexlms-page-header">
          <div className="edexlms-page-title">
            {intl.formatMessage({ id: "rewards" })}
          </div>
          <div className="edexlms-page-actions">
            {canAdd && (
              <TooltipButton
                label={intl.formatMessage({ id: "add_reward" })}
                icon="pi pi-plus"
                onClick={onAddClick}
                size="small"
                type="button"
                disabled={!canAdd}
                tooltipOptions={{ showOnDisabled: true, position: "bottom" }}
                tooltip={
                  canAdd ? "" : intl.formatMessage({ id: "no_permission" })
                }
              />
            )}
          </div>
        </div>
        <div className="edexlms-page-body">
          <div className="edexlms-search-filter-bar">
            <TextInput
              iconPosition="left"
              icon="pi-search"
              placeholder={intl.formatMessage({
                id: "search_rewards",
              })}
              input={{
                name: "search",
                value: namePattern,
                onChange: (e) => setNamePattern(e.target.value),
              }}
            />
            <TextInput
              iconPosition="left"
              icon="pi-search"
              placeholder={intl.formatMessage({
                id: "search_created_by",
              })}
              input={{
                name: "search",
                value: createdByNamePattern,
                onChange: (e) => setCreatedByNamePattern(e.target.value),
              }}
            />
          </div>

          <List
            data={Array.isArray(list) ? list : []}
            columns={columns}
            loading={requesting}
            totalRecords={totalCount || 0}
            pageQuery={query}
            handlePageChange={(pageData) => {
              if (
                query.pageSize !== pageData.rows ||
                query.pageNo !== pageData.page + 1
              ) {
                setQuery({
                  ...query,
                  pageNo: pageData.page + 1,
                  pageSize: pageData.rows,
                });
              }
            }}
            handleOnSortChange={(sortData) => {
              setQuery({
                ...query,
                pageNo: 1,
                sortColumn: sortData.sortField,
                sortBy: sortData.sortOrder === 1 ? "asc" : "desc",
              });
            }}
            selectedRecords={selectedRecords}
            setSelectedRecords={setSelectedRecords}
            enableSelection={false}
            handleRowClick={(data) =>
              navigate(
                `${RouteConstants.APP_ROUTE_REWARDS_EDIT.replace(
                  ":id",
                  data.data.id
                )}`
              )
            }
            selectionMode=""
          />
        </div>
        <ConfirmDialog />
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  list: state.rewards?.data?.list || [],
  totalCount: state.rewards?.data?.totalCount || 0,
  query: state.rewards?.query,
  requesting: state.rewards?.requesting,
  action: state.rewards?.action,
  user: state.self?.user,
});

const mapDispatchToProps = (dispatch) => ({
  listRequest: (data) => dispatch(listRequest(data)),
  setQuery: (data) => dispatch(setQuery(data)),
  deleteRequest: (data) => dispatch(deleteRequest(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(RewardList);
